import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, ArrowClockwise, CheckCircle } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import QuizResult from "./QuizResult";
import QuizContactModal from "./QuizContactModal";

const STORAGE_KEY = "ontenacity_quiz_progress_v1";
const API = process.env.REACT_APP_BACKEND_URL;

const QuizSection = ({ open, onOpenChange }) => {
  const [quizDef, setQuizDef] = useState(null);
  const [step, setStep] = useState("quiz"); // quiz | result
  const [currentQ, setCurrentQ] = useState(0); // 0-based index
  const [answers, setAnswers] = useState({}); // { 1: "A", 2: "B", ... }
  const [result, setResult] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const sectionRef = useRef(null);
  const questionRef = useRef(null);

  // Load quiz definition
  useEffect(() => {
    fetch(`${API}/api/quiz/definition`)
      .then((r) => r.json())
      .then(setQuizDef)
      .catch((e) => console.error("Failed to load quiz:", e));
  }, []);

  // Restore in-progress answers
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.answers && Object.keys(parsed.answers).length > 0) {
          setAnswers(parsed.answers);
          setCurrentQ(parsed.currentQ || 0);
          if (parsed.step) setStep(parsed.step);
        }
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Persist progress (anonymous only)
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ step, currentQ, answers }));
    } catch (e) {
      // ignore
    }
  }, [step, currentQ, answers]);

  // Announce question change for screen readers
  useEffect(() => {
    if (step === "quiz" && questionRef.current) {
      questionRef.current.focus();
    }
  }, [currentQ, step]);

  if (!quizDef) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-2xl bg-[#0A0A0B] border border-white/10 text-white p-8"
          data-testid="quiz-modal"
          data-lenis-prevent
        >
          <div className="text-center text-[#71717A] py-12">Loading quiz…</div>
        </DialogContent>
      </Dialog>
    );
  }

  const questions = quizDef.questions;
  const totalQ = questions.length;
  const q = questions[currentQ];
  const selectedLetter = answers[q?.number];
  const canContinue = !!selectedLetter;
  const isLast = currentQ === totalQ - 1;

  const scrollToSection = () => {
    // No-op — quiz is now shown inside a modal
  };

  const startQuiz = () => {
    setStep("quiz");
    setCurrentQ(0);
    setTimeout(scrollToSection, 100);
  };

  const selectAnswer = (letter) => {
    setAnswers((prev) => ({ ...prev, [q.number]: letter }));
  };

  const goBack = () => {
    if (currentQ > 0) {
      setCurrentQ(currentQ - 1);
    }
  };

  const goNext = () => {
    if (!canContinue) return;
    if (isLast) {
      // Score locally for immediate display (server will recompute on submit)
      computeAndShowResult();
    } else {
      setCurrentQ(currentQ + 1);
    }
  };

  const computeAndShowResult = () => {
    const answersList = questions.map((question) => ({
      question_number: question.number,
      letter: answers[question.number],
    }));
    const scored = scoreQuizClient(answersList, questions);
    setResult(scored);
    setStep("result");
    setTimeout(scrollToSection, 100);
  };

  const restartQuiz = () => {
    setAnswers({});
    setCurrentQ(0);
    setResult(null);
    setStep("quiz");
    sessionStorage.removeItem(STORAGE_KEY);
    setShowRestartConfirm(false);
  };

  const requestRestart = () => {
    if (Object.keys(answers).length > 0) {
      setShowRestartConfirm(true);
    } else {
      restartQuiz();
    }
  };

  const retakeQuiz = () => {
    setAnswers({});
    setCurrentQ(0);
    setResult(null);
    setStep("quiz");
    sessionStorage.removeItem(STORAGE_KEY);
    setTimeout(scrollToSection, 100);
  };

  const reviewAnswers = () => {
    setStep("quiz");
    setCurrentQ(0);
    setTimeout(scrollToSection, 100);
  };

  const openContactForm = () => setContactOpen(true);

  const handleSubmissionSuccess = () => {
    // Keep result on screen. Clear session storage since submission is complete.
    sessionStorage.removeItem(STORAGE_KEY);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-[#0A0A0B] border border-white/10 text-white p-6 md:p-8 max-h-[90vh] overflow-y-auto"
        data-testid="quiz-modal"
        data-lenis-prevent
      >
        <div ref={sectionRef}>
          <AnimatePresence mode="wait">
            {step === "quiz" && q && (
            <motion.div
              key={`q-${currentQ}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              data-testid={`quiz-question-${q.number}`}
            >
              {/* Progress + step counter */}
              <div className="flex items-center justify-between mb-8">
                <span className="text-xs text-[#00E5FF] font-mono tracking-wider">
                  Question {currentQ + 1} of {totalQ}
                </span>
                <button
                  onClick={requestRestart}
                  className="text-xs text-[#71717A] hover:text-white transition-colors flex items-center gap-1"
                  data-testid="quiz-restart-btn"
                >
                  <ArrowClockwise size={12} /> Start over
                </button>
              </div>

              {/* Progress bar */}
              <div className="w-full h-1 bg-white/5 rounded-full mb-6 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-[#00E5FF] to-[#06B6D4]"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentQ + 1) / totalQ) * 100}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>

              {/* Guidance shown before Q1 */}
              {currentQ === 0 && (
                <p className="mb-6 text-sm text-[#A1A1AA] p-4 rounded-lg bg-white/5 border border-white/5 leading-relaxed">
                  Think about a recent tool, service, or process your company introduced.
                  Choose the answer closest to what actually happened. Where several apply,
                  select the one that most influenced the decision.
                </p>
              )}

              {/* Question */}
              <h3
                ref={questionRef}
                tabIndex={-1}
                className="text-2xl md:text-3xl font-semibold text-white font-['Outfit'] mb-6 leading-tight outline-none"
                aria-live="polite"
              >
                {q.text}
              </h3>

              {/* Options as accessible radio group */}
              <div
                role="radiogroup"
                aria-label={`Question ${currentQ + 1}`}
                className="space-y-3 mb-8"
              >
                {q.options.map((opt) => {
                  const isSelected = selectedLetter === opt.letter;
                  const isUnknown = opt.letter === "UNKNOWN";
                  return (
                    <button
                      key={opt.letter}
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => selectAnswer(opt.letter)}
                      className={`w-full text-left p-4 md:p-5 rounded-xl border transition-all focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/50 ${
                        isSelected
                          ? "border-[#00E5FF] bg-[#00E5FF]/5"
                          : "border-white/10 bg-[#0F0F10] hover:border-white/20"
                      } ${isUnknown ? "border-dashed" : ""}`}
                      data-testid={`quiz-option-${q.number}-${opt.letter}`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                            isSelected ? "border-[#00E5FF] bg-[#00E5FF]" : "border-white/20"
                          }`}
                        >
                          {isSelected && <CheckCircle size={14} weight="fill" className="text-[#050505]" />}
                        </div>
                        <div className="flex-1">
                          <span
                            className={`text-sm md:text-base leading-relaxed ${
                              isSelected ? "text-white" : isUnknown ? "text-[#71717A]" : "text-[#FAFAFA]"
                            }`}
                          >
                            {opt.text}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={goBack}
                  className="text-[#A1A1AA] hover:text-white hover:bg-white/5 flex items-center gap-2"
                  data-testid="quiz-back-btn"
                >
                  <ArrowLeft size={16} />
                  Back
                </Button>
                <Button
                  onClick={goNext}
                  disabled={!canContinue}
                  className={`btn-primary rounded-full px-6 py-3 flex items-center gap-2 ${
                    !canContinue ? "opacity-40 cursor-not-allowed" : ""
                  }`}
                  data-testid="quiz-continue-btn"
                >
                  {isLast ? "See my starting style" : "Continue"}
                  <ArrowRight size={16} />
                </Button>
              </div>
            </motion.div>
          )}

          {step === "result" && result && (
            <QuizResult
              key="result"
              result={result}
              questions={questions}
              onStartJourney={openContactForm}
              onRetake={retakeQuiz}
              onReview={reviewAnswers}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Restart confirmation */}
      {showRestartConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#0F0F10] border border-white/10 rounded-xl p-6 max-w-sm w-full" data-testid="restart-confirm">
            <h3 className="text-lg font-semibold text-white mb-2">Start over?</h3>
            <p className="text-sm text-[#A1A1AA] mb-6">
              Your current answers will be cleared.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={() => setShowRestartConfirm(false)}
                className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button onClick={restartQuiz} className="btn-primary rounded-full px-5" data-testid="restart-confirm-yes">
                Start over
              </Button>
            </div>
          </div>
        </div>
      )}

      <QuizContactModal
        open={contactOpen}
        onOpenChange={setContactOpen}
        answers={Object.entries(answers).map(([qnum, letter]) => ({
          question_number: parseInt(qnum),
          letter,
        }))}
        onSuccess={handleSubmissionSuccess}
      />
      </DialogContent>
    </Dialog>
  );
};

// -------- Client-side scoring (mirrors server, used only for immediate display) --------
export function scoreQuizClient(answersList, questions) {
  const personaKeys = [
    "MEASURED_BUILDER",
    "EVIDENCE_FIRST",
    "BUDGET_FOCUSED",
    "GUIDED_IMPLEMENTER",
    "TEAM_LED",
    "STAGED_INVESTOR",
  ];
  const scores = Object.fromEntries(personaKeys.map((k) => [k, 0]));
  let substantive = 0;
  let unknownCount = 0;
  const answerRecords = [];

  for (const q of questions) {
    const ans = answersList.find((a) => a.question_number === q.number);
    const opt = q.options.find((o) => o.letter === ans.letter);
    if (opt.persona === null) {
      unknownCount += 1;
    } else {
      scores[opt.persona] += 1;
      substantive += 1;
    }
    answerRecords.push({
      question_number: q.number,
      question_text: q.text,
      letter: ans.letter,
      answer_text: opt.text,
      persona: opt.persona,
    });
  }

  const result = {
    scores,
    substantive_answers: substantive,
    unknown_count: unknownCount,
    answer_records: answerRecords,
    primary_persona: null,
    supporting_persona: null,
    blended_personas: null,
  };

  if (substantive < 3) {
    result.result_type = "INSUFFICIENT_HISTORY";
    return result;
  }

  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const maxScore = sorted[0][1];
  const maxPersonas = sorted.filter(([, s]) => s === maxScore).map(([p]) => p);

  if (maxScore >= 3 && maxPersonas.length === 1) {
    result.result_type = "PRIMARY";
    result.primary_persona = maxPersonas[0];
    const twoPoint = Object.entries(scores).filter(([p, s]) => s === 2 && p !== maxPersonas[0]).map(([p]) => p);
    if (twoPoint.length === 1) result.supporting_persona = twoPoint[0];
    return result;
  }

  const twoPointPersonas = Object.entries(scores).filter(([, s]) => s === 2).map(([p]) => p);
  if (maxScore === 2 && twoPointPersonas.length === 2) {
    result.result_type = "BLENDED";
    result.blended_personas = twoPointPersonas.sort();
    return result;
  }
  if (maxScore === 2 && twoPointPersonas.length === 1) {
    result.result_type = "TENTATIVE";
    result.primary_persona = twoPointPersonas[0];
    return result;
  }

  result.result_type = "FLEXIBLE";
  return result;
}

export default QuizSection;
